import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductVariationsProps } from './types';
import { normalizeProductId, extractNumericProductId, getVariantImageById, getColorFromHandle } from './utils';
import { ApiManager } from '../../services/ApiManager';
import { useSession } from '../../hooks/useSession';
import { useLocalization } from '../../context/LocalizationContext';

const ProductVariations: React.FC<ProductVariationsProps> = ({
  productData,
  currentProduct,
  selectedVariations,
  variantImages,
  labelColor,
  labelSizeChart,
  getPreOrderDate,
  labelPreOrder,
  onVariationChange,
  onColorVariantChange,
}) => {
  const navigate = useNavigate();
  const { getFormattedExperiments } = useSession();
  const { config } = useLocalization();
  const [colorVariantAvailability, setColorVariantAvailability] = useState<Record<string, boolean>>({});
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  if (!currentProduct) return null;

  const preOrderDate = getPreOrderDate();
  const formatPreOrderDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = `${config.language.toLowerCase()}-${config.country}`;
    return date.toLocaleDateString(locale, {
      month: 'short',
      year: 'numeric',
      day: 'numeric',
    });
  };

  const getPreOrderLabel = () => {
    if (!preOrderDate) return '';
    const formattedDate = formatPreOrderDate(preOrderDate);
    return labelPreOrder.replace('{date}', formattedDate);
  };

  useEffect(() => {
    const checkColorVariantAvailability = async () => {
      if (!currentProduct?.other_colors?.references?.edges?.length) {
        return;
      }

      const colorVariantIds = currentProduct.other_colors.references.edges
        .map((edge: any) => {
          const productId = edge.node.id;
          return extractNumericProductId(productId);
        })
        .filter(Boolean);

      if (colorVariantIds.length === 0) {
        return;
      }

      setIsCheckingAvailability(true);
      try {
        const availability = await ApiManager.products.checkProductsAvailability(
          colorVariantIds,
          getFormattedExperiments()
        );
        setColorVariantAvailability(availability);
      } catch (error) {
        console.warn('Failed to check color variant availability:', error);
        const defaultAvailability = colorVariantIds.reduce((acc: Record<string, boolean>, numericId: string) => {
          acc[numericId] = true;
          return acc;
        }, {} as Record<string, boolean>);
        setColorVariantAvailability(defaultAvailability);
      } finally {
        setIsCheckingAvailability(false);
      }
    };

    checkColorVariantAvailability();
  }, [currentProduct?.id, currentProduct?.other_colors]);

  const handleSizeGuideClick = () => {
    const rootPath = process.env.REACT_APP_ROOT_PATH || '';
    // Pass the current product ID as state so the size guide can navigate back correctly
    navigate(`${rootPath}/size-guide`, {
      state: {
        from: 'product',
        productId: currentProduct.id,
      },
    });
  };

  const renderColorVariations = () => {
    // Check if we should show color variants section
    if (!currentProduct.other_colors || currentProduct.other_colors.references.edges.length === 0) {
      return null;
    }

    // Check if there are any displayable color variants
    const currentProductGid = normalizeProductId(currentProduct.id);
    const currentProductVariantImage = getVariantImageById(currentProductGid, variantImages);

    const otherColorsWithImages = currentProduct.other_colors.references.edges.filter((edge: any) => {
      const variantImageUrl = getVariantImageById(edge.node.id, variantImages);
      const isNotCurrentProduct = edge.node.id !== currentProductGid;
      return variantImageUrl && isNotCurrentProduct;
    });

    // Only show section if we have current image + other colors, or multiple other colors
    const hasDisplayableVariants =
      (currentProductVariantImage && otherColorsWithImages.length > 0) || otherColorsWithImages.length > 1;

    if (!hasDisplayableVariants) {
      return null;
    }

    return (
      <div className="variation-section">
        <h3 className="variation-type">{labelColor}</h3>
        <div className="variation-options">
          {(() => {
            // Create array starting with current product, then other colors
            const allColorVariants = [];

            // Add current product first if it has an image
            if (currentProductVariantImage) {
              const currentVariantImageData = variantImages.find((item) => item.product.id === currentProductGid);
              const currentColorName = currentVariantImageData
                ? getColorFromHandle(currentVariantImageData.product.handle)
                : 'Current';

              allColorVariants.push(
                <button
                  key={currentProduct.id}
                  className="variation-image-button current-color"
                  disabled
                  title={`${currentColorName} variant (current)`}
                  aria-label={`Current ${currentColorName} color variant`}
                >
                  <img
                    src={
                      currentProductVariantImage.startsWith('//')
                        ? `https:${currentProductVariantImage}`
                        : currentProductVariantImage
                    }
                    alt={`${currentColorName} variant (current)`}
                    className="variant-image"
                  />
                </button>
              );
            }

            // Add other color variants
            const otherColorVariants = currentProduct.other_colors.references.edges
              .filter((edge: any) => {
                const variantImageUrl = getVariantImageById(edge.node.id, variantImages);
                const isNotCurrentProduct = edge.node.id !== currentProductGid;
                return variantImageUrl && isNotCurrentProduct; // Only include variants that have images and are not the current product
              })
              .map((edge: any) => {
                const variantImageUrl = getVariantImageById(edge.node.id, variantImages)!; // Non-null assertion since we filtered
                const variantImageData = variantImages.find((item) => item.product.id === edge.node.id);
                const colorName = variantImageData ? getColorFromHandle(variantImageData.product.handle) : 'Unknown';

                // Check availability for this color variant
                const numericId = extractNumericProductId(edge.node.id);
                const isAvailable = colorVariantAvailability[numericId] !== false; // Default to true if not yet checked
                const isCheckingThisVariant =
                  isCheckingAvailability && colorVariantAvailability[numericId] === undefined;

                return (
                  <button
                    key={edge.node.id}
                    className={`variation-image-button ${!isAvailable ? 'disabled' : ''} ${
                      isCheckingThisVariant ? 'checking' : ''
                    }`}
                    onClick={() => isAvailable && onColorVariantChange(edge.node.id)}
                    disabled={!isAvailable}
                    title={`${colorName} variant${!isAvailable ? ' (not available)' : ''}`}
                    aria-label={`Select ${colorName} color variant${!isAvailable ? ' (not available)' : ''}`}
                  >
                    <img
                      src={variantImageUrl.startsWith('//') ? `https:${variantImageUrl}` : variantImageUrl}
                      alt={`${colorName} variant`}
                      className="variant-image"
                    />
                  </button>
                );
              });

            return [...allColorVariants, ...otherColorVariants];
          })()}
        </div>
      </div>
    );
  };

  const renderRegularVariations = () => {
    return productData.variations.map((variation, index) => (
      <div key={variation.name} className="variation-section">
        <div className="variation-header">
          <h3 className="variation-type">{variation.name}</h3>
          {index === 0 && (
            <span className="size-guide-link" onClick={handleSizeGuideClick}>
              {labelSizeChart}
            </span>
          )}
        </div>

        {/* Pre-order information under first variation title */}
        {index === 0 && preOrderDate && (
          <div className="pre-order-section">
            <div className="pre-order-label">{getPreOrderLabel()}</div>
          </div>
        )}

        <div className="variation-options">
          {variation.values.map((value) => {
            // For multi-option products, we need to check if this specific option value
            // has any available variants when combined with currently selected options
            let isAvailable = true;

            if (currentProduct?.variants.edges) {
              // Create a test selection with the current option value
              const testSelection = {
                ...selectedVariations,
                [variation.name]: value,
              };

              // Check if there's at least one variant that matches this option value
              // and is available, considering current selections
              const hasAvailableVariant = currentProduct.variants.edges.some((edge) => {
                const variant = edge.node;

                if (!variant.availableForSale) {
                  return false;
                }

                // If we have selectedOptions data, use it for accurate matching
                if (variant.selectedOptions && variant.selectedOptions.length > 0) {
                  // Check if this variant matches the test selection
                  const variantMatchesTest = variant.selectedOptions.every((selectedOption: any) => {
                    const testValue = testSelection[selectedOption.name];
                    // If we have a selection for this option, it must match
                    if (testValue !== undefined) {
                      return testValue === selectedOption.value;
                    }
                    // If we don't have a selection for this option yet, allow any value
                    return true;
                  });

                  // Also check that this variant actually has the option value we're testing
                  const hasMatchingOption = variant.selectedOptions.some((selectedOption: any) => {
                    return selectedOption.name === variation.name && selectedOption.value === value;
                  });

                  return variantMatchesTest && hasMatchingOption;
                }

                // Fallback: for single-option products or if selectedOptions is not available
                return variant.title === value || variant.title.includes(value);
              });

              isAvailable = hasAvailableVariant;
            }

            return (
              <button
                key={value}
                className={`variation-button ${selectedVariations[variation.name] === value ? 'selected' : ''} ${
                  !isAvailable ? 'disabled' : ''
                }`}
                onClick={() => isAvailable && onVariationChange(variation.name, value)}
                disabled={!isAvailable}
              >
                {value}
              </button>
            );
          })}
        </div>
      </div>
    ));
  };

  return (
    <>
      {renderColorVariations()}

      {productData.variations.length > 0 && renderRegularVariations()}
    </>
  );
};

export default ProductVariations;
